import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { UserPlus, Loader2, Check, X, RefreshCw, Mail, Phone, Plane, Clock } from 'lucide-react';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import { hashPassword } from '../../utils/crypto';
import { useAuth } from '../../context/AuthContext';

const randomPassword = () => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  return Array.from({ length: 12 }, () => charset[Math.floor(Math.random() * charset.length)]).join('');
};

const STATUS_STYLES = {
  PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  APPROVED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// User-creation requests raised by employees. Admins and super admins can
// approve (supplying the credentials) or reject; employees see their own
// requests and where they stand.
const CustomerRequestsPanel = ({ refreshKey = 0, onApproved }) => {
  const { user } = useAuth();
  const canReview = user?.role === 'admin' || user?.role === 'super_admin';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null); // { request, username, password }
  const [rejecting, setRejecting] = useState(null); // { request, reason }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiCall(`${API_ENDPOINTS.CUSTOMER_REQUESTS}?status=PENDING`, { method: 'GET' });
      const result = await res.json();
      if (res.ok && result.success) setRequests(Array.isArray(result.data) ? result.data : []);
    } catch {
      // the panel is supplementary — stay quiet and just show nothing
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const submitApprove = async (e) => {
    e.preventDefault();
    const { request, username, password } = approving;
    if (username.trim().length < 3) { toast.error('Username must be at least 3 characters'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setBusy(true);
    try {
      const res = await apiCall(`${API_ENDPOINTS.CUSTOMER_REQUESTS}/${request.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), password: await hashPassword(password) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        toast.error(data?.message || 'Could not approve the request');
        return;
      }
      toast.success(`${request.name} created`);
      setRequests(prev => prev.filter(r => r.id !== request.id));
      setApproving(null);
      onApproved?.(request);
    } catch {
      toast.error('Unable to reach the server');
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async (e) => {
    e.preventDefault();
    const { request, reason } = rejecting;
    setBusy(true);
    try {
      const res = await apiCall(`${API_ENDPOINTS.CUSTOMER_REQUESTS}/${request.id}/reject`, {
        method: 'POST',
        body: JSON.stringify(reason.trim() ? { rejectionReason: reason.trim() } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        toast.error(data?.message || 'Could not reject the request');
        return;
      }
      toast.success('Request rejected');
      setRequests(prev => prev.filter(r => r.id !== request.id));
      setRejecting(null);
    } catch {
      toast.error('Unable to reach the server');
    } finally {
      setBusy(false);
    }
  };

  if (loading || requests.length === 0) return null;

  return (
    <>
      <div className="mb-5 rounded-2xl border-2 border-blue-200 dark:border-blue-900/60 bg-blue-50/60 dark:bg-blue-950/20 p-3 sm:p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 px-1">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
              <UserPlus size={16} className="text-blue-600 dark:text-blue-400" />
            </span>
            <div>
              <h4 className="font-bold text-blue-900 dark:text-blue-300 leading-tight">
                User Requests <span className="font-semibold text-blue-500 dark:text-blue-400">({requests.length})</span>
              </h4>
              <p className="text-xs text-blue-600/80 dark:text-blue-400/80">
                {canReview ? 'Employees have asked you to create these users.' : 'Sent to your admin — waiting for them to create these users.'}
              </p>
            </div>
          </div>
          <button onClick={load} title="Refresh" className="p-1.5 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition">
            <RefreshCw size={15} />
          </button>
        </div>

        {requests.map((r) => (
          <div key={r.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3.5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-gray-900 dark:text-white">{r.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[r.status] || STATUS_STYLES.PENDING}`}>
                    {r.status}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1"><Mail size={12} />{r.email}</span>
                  <span className="flex items-center gap-1"><Phone size={12} />{r.phNum}</span>
                  <span className="flex items-center gap-1 font-mono"><Plane size={12} />{r.awbNumber}</span>
                  <span className="uppercase font-semibold">{r.service}</span>
                  {r.createdAt && <span className="flex items-center gap-1"><Clock size={12} />{formatWhen(r.createdAt)}</span>}
                </div>
              </div>
              {canReview && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setRejecting({ request: r, reason: '' })}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-red-600 dark:text-red-400 border-2 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 transition"
                  >
                    <X size={14} /> Reject
                  </button>
                  <button
                    onClick={() => setApproving({ request: r, username: '', password: '' })}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition"
                  >
                    <Check size={14} /> Approve
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {approving && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !busy && setApproving(null)}>
          <form onSubmit={submitApprove} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-emerald-600 to-green-600">
              <h3 className="text-base font-bold text-white">Approve Request</h3>
              <p className="text-xs text-emerald-100 mt-0.5">Set the login for {approving.request.name}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Username <span className="text-red-500">*</span></label>
                <input value={approving.username} onChange={(e) => setApproving(p => ({ ...p, username: e.target.value.replace(/\s/g, '') }))}
                  disabled={busy} autoComplete="new-password" placeholder="Unique login name"
                  className="w-full px-3.5 py-2.5 rounded-lg border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 text-sm font-medium outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input value={approving.password} onChange={(e) => setApproving(p => ({ ...p, password: e.target.value }))}
                    disabled={busy} autoComplete="new-password" placeholder="Set a password or generate one"
                    className="w-full px-3.5 py-2.5 pr-11 rounded-lg border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 text-sm font-medium outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                  <button type="button" onClick={() => setApproving(p => ({ ...p, password: randomPassword() }))} title="Generate a strong password"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                    <RefreshCw size={15} />
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  <button type="button" onClick={() => setApproving(p => ({ ...p, password: randomPassword() }))}
                    className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                    Generate a strong password
                  </button>
                  {' '}— share it with the user, it isn't shown again.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2.5 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
              <button type="button" onClick={() => setApproving(null)} disabled={busy}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50">Cancel</button>
              <button type="submit" disabled={busy}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-md transition disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Create User
              </button>
            </div>
          </form>
        </div>
      )}

      {rejecting && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !busy && setRejecting(null)}>
          <form onSubmit={submitReject} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-red-600 to-rose-600">
              <h3 className="text-base font-bold text-white">Reject Request</h3>
              <p className="text-xs text-red-100 mt-0.5">{rejecting.request.name} · AWB {rejecting.request.awbNumber}</p>
            </div>
            <div className="p-5">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Reason <span className="normal-case font-normal">(optional)</span></label>
              <textarea value={rejecting.reason} onChange={(e) => setRejecting(p => ({ ...p, reason: e.target.value }))}
                disabled={busy} rows={3} maxLength={1000} placeholder="e.g. Duplicate of an existing customer"
                className="w-full px-3.5 py-2.5 rounded-lg border-2 border-gray-200 dark:border-gray-600 focus:border-red-500 dark:focus:border-red-400 text-sm outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-none" />
            </div>
            <div className="flex justify-end gap-2.5 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
              <button type="button" onClick={() => setRejecting(null)} disabled={busy}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50">Cancel</button>
              <button type="submit" disabled={busy}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-md transition disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />} Reject
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default CustomerRequestsPanel;
